import { NavLink, Navigate, Outlet } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarClock, Clock, Dumbbell, LineChart, LogOut, MessageCircle, Users, Wallet } from "lucide-react";
import { TRAINER } from "../../constants/testIds";
import { useTrainerData } from "../../contexts/TrainerDataContext";
import { GymLoader } from "../common/GymLoader";
import { fadeIn } from "../../lib/motion";
import { EmulationFlag } from "../emulation/EmulationFlag";
import { OnboardingTourOverlay, OnboardingHelpButton } from "../onboarding/OnboardingTour";
import { useOnboardingTour } from "../onboarding/useOnboardingTour";
import { TRAINER_STEPS } from "../onboarding/onboardingSteps";

const NAV_ITEMS = [
  { key: "today", label: "Today", path: "/trainer/today", icon: CalendarClock },
  { key: "schedule", label: "Schedule", path: "/trainer/schedule", icon: Dumbbell },
  { key: "availability", label: "Availability", path: "/trainer/availability", icon: Clock },
  { key: "clients", label: "My Clients", path: "/trainer/clients", icon: Users },
  { key: "earnings", label: "Earnings", path: "/trainer/earnings", icon: Wallet },
  { key: "performance", label: "Performance", path: "/trainer/performance", icon: LineChart },
  { key: "message", label: "Message", path: "/trainer/message", icon: MessageCircle },
];

// UX-TR-01 §0 "closer to the Front Desk register than a showcase app — a between-sessions
// workhorse": same flat solid-bar shell language as DeskShell (no glass/blur, no video), just a
// second nav row of tabs — a trainer lives on this screen on a phone/tablet between clients, not
// browsing a catalog.
export function TrainerShell() {
  const { me, provider, loading, error, logout } = useTrainerData();
  const reducedMotion = useReducedMotion();
  const tour = useOnboardingTour("trainer", me?.login_count, me?.id);

  if (loading) return <GymLoader fullPage label="Loading Trainer App…" testId="trainer-shell-loading" />;
  if (error) return <Navigate to="/login" replace />;

  return (
    <div data-testid={TRAINER.shell} style={{ background: "#F4F3EF", minHeight: "100vh", paddingTop: me?.emulation?.active ? "var(--emulation-flag-height)" : undefined }}>
      <EmulationFlag emulation={me?.emulation} orgId={me?.organization_id} gymName={me?.organization_display_name} />
      <div className="flex items-center justify-between px-5 py-3" style={{ background: "#141416", color: "#fff" }}>
        <div className="flex items-center gap-3">
          <span className="font-bold text-sm tracking-tight">FitFrikArena</span>
          <span className="opacity-40">·</span>
          <span data-testid={TRAINER.providerName} className="text-sm font-medium">{provider?.name || "Trainer"}</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm opacity-70">{me?.name}</span>
          <button
            data-testid={TRAINER.logoutButton}
            onClick={logout}
            className="flex items-center gap-1.5 text-sm font-medium opacity-80 hover:opacity-100 transition-opacity scarlet-focusable"
          >
            <LogOut size={14} /> Log out
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 px-4 overflow-x-auto" style={{ background: "#1E1E22", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.key}
            to={item.path}
            data-testid={TRAINER.navItem(item.key)}
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-4 py-3 text-sm font-semibold border-b-2 transition-colors shrink-0 ${isActive ? "border-[#D6202B] text-white" : "border-transparent text-white/50 hover:text-white/80"}`
            }
          >
            <item.icon size={14} /> {item.label}
          </NavLink>
        ))}
      </div>

      <main className="p-5 max-w-5xl mx-auto">
        <motion.div {...fadeIn(reducedMotion)}>
          <Outlet />
        </motion.div>
      </main>
      {!me?.emulation?.active && (
        <>
          <OnboardingTourOverlay steps={TRAINER_STEPS} tour={tour} />
          <OnboardingHelpButton onClick={tour.restart} />
        </>
      )}
    </div>
  );
}
